import { Link } from "react-router-dom";
import { Linkedin, Twitter, Facebook, Instagram, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { useSettings, useServices, useProducts } from "@/hooks/useSiteData";

export const Footer = () => {
  const { data: settings } = useSettings();
  const { data: services = [] } = useServices();
  const { data: products = [] } = useProducts();
  const year = new Date().getFullYear();
  const social = settings?.social || {};
  const socials = [
    { key: "linkedin", Icon: Linkedin, href: social.linkedin },
    { key: "twitter", Icon: Twitter, href: social.twitter },
    { key: "facebook", Icon: Facebook, href: social.facebook },
    { key: "instagram", Icon: Instagram, href: social.instagram },
  ].filter((s) => s.href);

  return (
    <footer className="ink-pattern relative overflow-hidden" data-testid="site-footer">
      <div className="container-x relative pt-16 lg:pt-24">
        <div className="grid gap-12 border-b border-white/10 pb-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link to="/" aria-label="Jonaren Technology home" data-testid="footer-logo"><img src={settings?.logo_white || "/assets/logo_white.png"} alt="Jonaren Technology" className="h-10 w-auto" /></Link>
            <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-white/65">{settings?.hero_subtitle || "Enterprise IT services, consulting and products since 2010."}</p>
            {socials.length > 0 && (
              <div className="mt-7 flex gap-2">
                {socials.map(({ key, Icon, href }) => (
                  <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={key} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors hover:border-primary hover:bg-primary hover:text-white" data-testid={`footer-social-${key}`}><Icon className="h-4 w-4" /></a>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-2">
            <h3 className="font-heading text-lg font-medium">Company</h3>
            <ul className="mt-5 space-y-3 text-[15px]">
              {[["/about", "About us"], ["/services", "Services"], ["/products", "Products"], ["/industries", "Industries"], ["/contact", "Contact"]].map(([to, label]) => (
                <li key={to}><Link to={to} className="text-white/65 transition-colors hover:text-white" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <h3 className="font-heading text-lg font-medium">Services</h3>
            <ul className="mt-5 space-y-3 text-[15px]">
              {services.slice(0, 8).map((s) => <li key={s.id}><Link to={`/services/${s.slug}`} className="text-white/65 transition-colors hover:text-white">{s.title}</Link></li>)}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <h3 className="font-heading text-lg font-medium">Products</h3>
            <ul className="mt-5 space-y-3 text-[15px]">
              {products.map((p) => <li key={p.id}><Link to={`/products/${p.slug}`} className="text-white/65 transition-colors hover:text-white">{p.name}</Link></li>)}
            </ul>
            <h3 className="mt-9 font-heading text-lg font-medium">Get in touch</h3>
            <ul className="mt-5 space-y-3 text-[15px] text-white/65">
              {settings?.emails?.[0] && <li className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-primary" /><a href={`mailto:${settings.emails[0]}`} className="hover:text-white" data-testid="footer-email">{settings.emails[0]}</a></li>}
              {settings?.phones?.[0] && <li className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-primary" /><a href={`tel:${settings.phones[0].replace(/\s+/g, "")}`} className="hover:text-white" data-testid="footer-phone">{settings.phones[0]}</a></li>}
              {settings?.offices?.slice(0, 2).map((o) => <li key={o.city} className="flex items-start gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{o.city}, {o.country} &middot; {o.label}</span></li>)}
            </ul>
          </div>
        </div>
        <div className="flex flex-col items-start justify-between gap-4 py-6 text-[13px] text-white/50 sm:flex-row sm:items-center">
          <p>&copy; {year} {settings?.company_name || "Jonaren Technology Pvt Ltd"}. All rights reserved.</p>
          <Link to="/contact" className="inline-flex items-center gap-1.5 text-white/70 transition-colors hover:text-white" data-testid="footer-cta-link">Start a project <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </div>
        <div className="pointer-events-none select-none overflow-hidden" aria-hidden="true">
          <p className="ghost-text -mb-5 text-center whitespace-nowrap font-heading text-[clamp(3rem,14vw,17rem)] font-bold leading-none tracking-tight lg:-mb-8">JONAREN</p>
        </div>
      </div>
    </footer>
  );
};
